/**
 * AudioPlayer Component
 * 
 * Plays generated audio with:
 * - Progress bar with seek functionality
 * - Voice alternation (male/female on each play)
 * - Sentence-level playback support
 * - Download capability
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { 
  Play, 
  Pause, 
  Volume2, 
  VolumeX, 
  Download,
  SkipBack,
  SkipForward,
  RefreshCw,
  User,
  Users
} from 'lucide-react';
import { Button } from '../ui/button';
import { Slider } from '../ui/slider';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function AudioPlayer({ 
  textId,
  audioFemaleUrl,
  audioMaleUrl,
  hasSentenceAudio = false,
  sentenceCount = 0,
  onPlayStart,
  onPlayEnd,
  onTimeUpdate,
  compact = false,
}) {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  // Voice alternation
  const [currentVoice, setCurrentVoice] = useState('female'); // 'female' or 'male'
  const [audioUrl, setAudioUrl] = useState(audioFemaleUrl);
  
  // Playback speed
  const [playbackRate, setPlaybackRate] = useState(1.0);
  const [showSpeedPicker, setShowSpeedPicker] = useState(false);
  
  // Sentence mode
  const [sentenceMode, setSentenceMode] = useState(false);
  const [currentSentence, setCurrentSentence] = useState(0);
  const [sentenceUrls, setSentenceUrls] = useState([]);

  // Initialize audio element
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  // Apply playback rate when it changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  // Update audio URL when voice changes
  useEffect(() => {
    if (currentVoice === 'female' && audioFemaleUrl) {
      setAudioUrl(audioFemaleUrl);
    } else if (currentVoice === 'male' && audioMaleUrl) {
      setAudioUrl(audioMaleUrl);
    }
  }, [currentVoice, audioFemaleUrl, audioMaleUrl]);

  // Load sentence audio metadata
  const loadSentenceMetadata = useCallback(async () => {
    if (!textId || !hasSentenceAudio) return;
    
    try {
      const res = await fetch(`${API_BASE}/api/texts/${textId}/audio-metadata`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        if (data.sentences) {
          setSentenceUrls(data.sentences);
        }
      }
    } catch (err) {
      console.error('Failed to load sentence metadata:', err);
    }
  }, [textId, hasSentenceAudio]);

  useEffect(() => {
    if (hasSentenceAudio) {
      loadSentenceMetadata();
    }
  }, [hasSentenceAudio, loadSentenceMetadata]);

  // Alternate voice on play
  const alternateVoice = () => {
    const newVoice = currentVoice === 'female' ? 'male' : 'female';
    setCurrentVoice(newVoice);
    return newVoice;
  };

  // Handle play/pause
  const togglePlay = async () => {
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
      setShowSpeedPicker(false);
      return;
    }

    // Show speed picker instead of playing immediately
    setShowSpeedPicker(true);
  };

  const startPlayAtRate = async (rate) => {
    setPlaybackRate(rate);
    setShowSpeedPicker(false);
    if (!audioRef.current) return;

    // Alternate voice on each play
    const newVoice = alternateVoice();
    const newUrl = newVoice === 'female' ? audioFemaleUrl : audioMaleUrl;
    
    if (!newUrl) {
      toast.error(`No ${newVoice} audio available`);
      return;
    }
    
    // If URL changed, reload
    if (newUrl !== audioRef.current.src) {
      setIsLoading(true);
      audioRef.current.src = newUrl;
      audioRef.current.load();
    }

    audioRef.current.playbackRate = rate;
    
    try {
      await audioRef.current.play();
      setIsPlaying(true);
      onPlayStart?.();
    } catch (err) {
      console.error('Play failed:', err);
      toast.error('Failed to play audio');
    }
  };

  // Handle time update
  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
      onTimeUpdate?.(audioRef.current.currentTime, duration);
    }
  };

  // Handle loaded metadata
  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
      setIsLoading(false);
    }
  };

  // Handle ended
  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
    onPlayEnd?.();
    
    // In sentence mode, auto-advance
    if (sentenceMode && currentSentence < sentenceUrls.length - 1) {
      setCurrentSentence(prev => prev + 1);
      // Auto-play next sentence after short delay
      setTimeout(() => {
        if (audioRef.current) {
          const nextUrl = currentVoice === 'female' 
            ? sentenceUrls[currentSentence + 1]?.female_url 
            : sentenceUrls[currentSentence + 1]?.male_url;
          if (nextUrl) {
            audioRef.current.src = nextUrl;
            audioRef.current.play();
            setIsPlaying(true);
          }
        }
      }, 300);
    }
  };

  // Seek to position
  const handleSeek = (value) => {
    if (audioRef.current) {
      const newTime = (value[0] / 100) * duration;
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  // Toggle mute
  const toggleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  // Format time
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Download audio
  const handleDownload = () => {
    const url = currentVoice === 'female' ? audioFemaleUrl : audioMaleUrl;
    if (!url) {
      toast.error('No audio available to download');
      return;
    }
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `audio_${textId}_${currentVoice}.mp3`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Download started');
  };

  // Skip to previous/next sentence
  const prevSentence = () => {
    if (currentSentence > 0) {
      setCurrentSentence(prev => prev - 1);
    }
  };

  const nextSentence = () => {
    if (currentSentence < sentenceUrls.length - 1) {
      setCurrentSentence(prev => prev + 1);
    }
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  if (compact) {
    return (
      <div className="flex items-center gap-2" data-testid="audio-player-compact">
        <audio
          ref={audioRef}
          src={audioUrl}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onEnded={handleEnded}
          onCanPlay={() => setIsLoading(false)}
        />
        
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={togglePlay}
          disabled={!audioFemaleUrl && !audioMaleUrl}
          data-testid="audio-play-btn"
        >
          {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
        </Button>
        
        <div className="flex-1 min-w-[100px]">
          <Slider
            value={[progress]}
            max={100}
            step={0.1}
            onValueChange={handleSeek}
            className="cursor-pointer"
            data-testid="audio-progress-slider"
          />
        </div>
        
        <span className="text-xs text-muted-foreground w-16 text-right">
          {formatTime(currentTime)} / {formatTime(duration)}
        </span>
        
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          onClick={() => setCurrentVoice(currentVoice === 'female' ? 'male' : 'female')}
          title={`Switch to ${currentVoice === 'female' ? 'male' : 'female'} voice`}
          data-testid="voice-toggle-btn"
        >
          {currentVoice === 'female' ? (
            <User className="h-3 w-3" />
          ) : (
            <Users className="h-3 w-3" />
          )}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3 p-4 bg-muted/30 rounded-lg" data-testid="audio-player">
      <audio
        ref={audioRef}
        src={audioUrl}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        onCanPlay={() => setIsLoading(false)}
      />
      
      {/* Progress Bar */}
      <div className="space-y-1">
        <Slider
          value={[progress]}
          max={100}
          step={0.1}
          onValueChange={handleSeek}
          className="cursor-pointer"
          data-testid="audio-progress-slider"
        />
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(duration)}</span>
        </div>
      </div>
      
      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {/* Sentence navigation (if available) */}
          {sentenceMode && sentenceUrls.length > 0 && (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={prevSentence}
                disabled={currentSentence === 0}
                data-testid="audio-prev-sentence-btn"
              >
                <SkipBack className="h-4 w-4" />
              </Button>
              <span className="text-xs text-muted-foreground">
                {currentSentence + 1}/{sentenceUrls.length}
              </span>
            </>
          )}
          
          {/* Play/Pause */}
          <div className="relative">
            <Button
              variant="default"
              size="icon"
              className="h-10 w-10"
              onClick={togglePlay}
              disabled={!audioFemaleUrl && !audioMaleUrl}
              data-testid="audio-play-btn"
            >
              {isLoading ? (
                <RefreshCw className="h-5 w-5 animate-spin" />
              ) : isPlaying ? (
                <Pause className="h-5 w-5" />
              ) : (
                <Play className="h-5 w-5" />
              )}
            </Button>
            {showSpeedPicker && !isPlaying && (
              <div
                className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 bg-slate-900 border border-slate-700 rounded-lg shadow-xl p-2 min-w-[110px]"
                data-testid="speed-picker"
              >
                <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide text-center mb-1.5">Speed</div>
                {[
                  { label: 'Normal', rate: 1.0, pct: '100%' },
                  { label: 'Slow',   rate: 0.8, pct: '80%'  },
                  { label: 'Slower', rate: 0.7, pct: '70%'  },
                ].map(({ label, rate, pct }) => (
                  <button
                    key={rate}
                    onClick={() => startPlayAtRate(rate)}
                    className={`w-full flex items-center justify-between px-2 py-1 rounded text-xs transition-colors hover:bg-slate-700 ${playbackRate === rate ? 'text-amber-400 font-semibold' : 'text-slate-300'}`}
                    data-testid={`speed-${label.toLowerCase()}-btn`}
                  >
                    <span>{label}</span>
                    <span className="text-slate-500">{pct}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          
          {sentenceMode && sentenceUrls.length > 0 && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={nextSentence}
              disabled={currentSentence === sentenceUrls.length - 1}
              data-testid="audio-next-sentence-btn"
            >
              <SkipForward className="h-4 w-4" />
            </Button>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          {/* Voice indicator/toggle */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentVoice(currentVoice === 'female' ? 'male' : 'female')}
            className="text-xs gap-1"
            data-testid="voice-toggle-btn"
          >
            {currentVoice === 'female' ? (
              <>
                <User className="h-3 w-3" />
                Female
              </>
            ) : (
              <>
                <Users className="h-3 w-3" />
                Male
              </>
            )}
          </Button>
          
          {/* Volume */}
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={toggleMute}
            data-testid="volume-btn"
          >
            {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
          </Button>
          
          {/* Download */}
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={handleDownload}
            disabled={!audioFemaleUrl && !audioMaleUrl}
            title="Download audio"
            data-testid="download-btn"
          >
            <Download className="h-4 w-4" />
          </Button>
        </div>
      </div>
      
      {/* Sentence mode toggle */}
      {hasSentenceAudio && sentenceUrls.length > 0 && (
        <div className="flex items-center gap-2 pt-2 border-t border-border">
          <Button
            variant={sentenceMode ? "secondary" : "ghost"}
            size="sm"
            onClick={() => setSentenceMode(!sentenceMode)}
            className="text-xs"
            data-testid="sentence-mode-toggle"
          >
            {sentenceMode ? 'Full Text' : 'By Sentence'}
          </Button>
          {sentenceMode && (
            <span className="text-xs text-muted-foreground">
              Playing sentence by sentence
            </span>
          )}
        </div>
      )}
    </div>
  );
}


